import React, { PropsWithChildren, useCallback, useContext, useEffect, useRef } from 'react';
import {
  Chat,
  GPT_MODEL_ADVANCED,
  GPT_MODEL_BASIC,
  GptModel,
  LibraryPromptWizardStep,
  Prompt,
  PromptVariableStringValueOrAdhoc,
} from '../types';
import { canUseAdvancedGptModel, createNewChat } from '../util';
import { useFetchedBillingCustomer } from '../../billing/hooks/useFetchedBillingCustomer';
import { useChatRealmContext } from './chatRealmContext';
import {
  CHAT_TYPE_BUSINESS_PLAN,
  CHAT_TYPE_CREATE_PROGRAM,
  CHAT_TYPE_GENERAL,
  ChatType,
} from '../dto';
import { BusinessPlanSurveySectionGuidanceKeys } from '../../businessplan/types';

export type LibraryPromptWizardStepState = {
  index: number;
  adhocValues: PromptVariableStringValueOrAdhoc[];
  selectedValues: PromptVariableStringValueOrAdhoc[];
  addNewMode: boolean;
  newValueDescription: string;
};

export type LibraryPromptWizardState = {
  steps: LibraryPromptWizardStep[];
  currentStepState: LibraryPromptWizardStepState;
  prompt: Prompt;
};

export type ChatLocalData = {
  rawContent: string;
  plainTextContent: string;
  libraryPromptWizardState: LibraryPromptWizardState | null;
};

type ChatContextProps = {
  chatTypeAndSubtype: { chatType: ChatType; chatSubtype: string | null };
  getChatContext: (chatId: string) => ChatLocalData;
  setChatContext: (chatId: string, data: Partial<ChatLocalData>) => void;
  getCurrentChat: () => Chat;
  setCurrentChat: (chat: Chat) => void;
  getSelectedCategory: () => string | null;
  setSelectedCategory: (category: string | null) => void;
  getCurrentModel: () => GptModel;
  setCurrentModel: (model: GptModel) => void;
};

type ChatContextsProps = Record<string, ChatContextProps>;

const getChatContextName = (chatType: ChatType, chatSubtype: string | null) =>
  `${chatType}--${chatSubtype ?? 'common'}`;

const ChatContexts = React.createContext<ChatContextsProps>({});

export const useChatContext = (): ChatContextProps => {
  const { chatType, chatSubtype } = useChatRealmContext();
  const chatContextsProps = useContext(ChatContexts);
  if (!chatType) throw Error('No chat realm here.');
  return chatContextsProps[getChatContextName(chatType, chatSubtype)];
};

const useSingleRealmChatContext = (
  chatType: ChatType,
  chatSubtype: string | null
): Omit<ChatContextProps, 'getCurrentModel' | 'setCurrentModel'> => {
  const chatTypeAndSubtype = { chatType, chatSubtype };
  const chats = useRef(new Map<string, ChatLocalData>());
  const getChatContext = (chatId: string) =>
    chats.current.get(chatId) ?? {
      rawContent: '',
      plainTextContent: '',
      libraryPromptWizardState: null,
    };
  const setChatContext = (chatId: string, data: Partial<ChatLocalData>) =>
    chats.current.set(chatId, { ...getChatContext(chatId), ...data });

  const currentChat = useRef(createNewChat(chatType, chatSubtype));
  const getCurrentChat = useCallback(() => currentChat.current, []);
  const setCurrentChat = useCallback((chat: Chat) => (currentChat.current = chat), [currentChat]);

  const selectedCategory = useRef<string | null>(null);
  const getSelectedCategory = useCallback(() => selectedCategory.current, []);
  const setSelectedCategory = useCallback((categoryName: string | null) => {
    selectedCategory.current = categoryName;
  }, []);

  return {
    chatTypeAndSubtype,
    getChatContext,
    setChatContext,
    getCurrentChat,
    setCurrentChat,
    getSelectedCategory,
    setSelectedCategory,
  };
};

export const ChatContextsProvider = (props: PropsWithChildren) => {
  const { children } = props;

  const growegyAIRealmContext = useSingleRealmChatContext(CHAT_TYPE_GENERAL, null);
  const programsRealmContext = useSingleRealmChatContext(CHAT_TYPE_CREATE_PROGRAM, null);

  const currentModel = useRef<GptModel>(GPT_MODEL_BASIC);
  const getCurrentModel = useCallback(() => currentModel.current, []);
  const setCurrentModel = useCallback((model: GptModel) => {
    currentModel.current = model;
  }, []);

  const acc: ChatContextsProps = {};
  for (const key of BusinessPlanSurveySectionGuidanceKeys) {
    acc[getChatContextName(CHAT_TYPE_BUSINESS_PLAN, key)] = {
      // eslint-disable-next-line react-hooks/rules-of-hooks
      ...useSingleRealmChatContext(CHAT_TYPE_BUSINESS_PLAN, key),
      getCurrentModel,
      setCurrentModel,
    };
  }

  const {
    query: { data: customer },
  } = useFetchedBillingCustomer();
  const firstCustomerLoad = useRef(true);
  useEffect(() => {
    if (customer && firstCustomerLoad) {
      if (canUseAdvancedGptModel(customer)) {
        setCurrentModel(GPT_MODEL_ADVANCED);
      }
      firstCustomerLoad.current = false;
    }
  }, [customer, firstCustomerLoad, setCurrentModel]);

  return (
    <ChatContexts.Provider
      value={{
        [getChatContextName(CHAT_TYPE_GENERAL, null)]: {
          ...growegyAIRealmContext,
          getCurrentModel,
          setCurrentModel,
        },
        [getChatContextName(CHAT_TYPE_CREATE_PROGRAM, null)]: {
          ...programsRealmContext,
          getCurrentModel,
          setCurrentModel,
        },
        ...acc,
      }}
    >
      {children}
    </ChatContexts.Provider>
  );
};
